// Thirvusoft Theme — login page enhancements (web_include_js).
//
// Runs on every website page (web_include_js) but only acts on /login
// (body[data-path="login"]). thirvusoft_theme.website.update_website_context adds
// `ts-login ts-login-theme-<slug>` to <body> server-side; `ts-login-off` means the admin
// switched the theme off, so the stock page is left untouched.
//
// Adds, idempotently (safe if the script runs twice):
//   * html[data-ts-theme=<slug>] so the site default palette tokens apply;
//   * an animated brand panel (first child of #page-login);
//   * "Powered by Thirvusoft Private Limited" under the card;
//   * a busy spinner on the visible section's primary button while a request runs
//     (login.js → frappe.call; request.js toggles body[data-ajax-state]).
(function () {
	"use strict";

	const body = document.body;
	if (!body || body.getAttribute("data-path") !== "login") return;
	if (body.classList.contains("ts-login-off")) return;

	const THEMES = ["aurora", "ocean", "sunset", "forest", "midnight", "graphite"];
	const POWERED_URL = "https://www.thirvusoft.com";
	const WORDMARK_URL = "/assets/thirvusoft_theme/images/thirvusoft_wordmark.png";
	const t = (text) => (typeof window.__ === "function" ? window.__(text) : text);

	// Fallback when the server hook is not active (e.g. hooks not migrated yet).
	if (!body.classList.contains("ts-login")) {
		body.classList.add("ts-login", "ts-login-theme-aurora");
	}

	const theme_class = Array.from(body.classList).find((c) => c.startsWith("ts-login-theme-"));
	const slug = theme_class ? theme_class.slice("ts-login-theme-".length) : "aurora";
	document.documentElement.setAttribute("data-ts-theme", THEMES.includes(slug) ? slug : "aurora");

	const page = document.getElementById("page-login");
	if (!page) return;

	const el = (tag, cls, text) => {
		const node = document.createElement(tag);
		if (cls) node.className = cls;
		if (text) node.textContent = text;
		return node;
	};

	const check_icon = () => {
		const ns = "http://www.w3.org/2000/svg";
		const svg = document.createElementNS(ns, "svg");
		svg.setAttribute("viewBox", "0 0 24 24");
		svg.setAttribute("aria-hidden", "true");
		const path = document.createElementNS(ns, "path");
		path.setAttribute("d", "M5 12.5l4.2 4.2L19 7");
		svg.appendChild(path);
		return svg;
	};

	// ---- "business growing" illustration (desktop brand panel, decorative) ------------------
	// Bars rise one by one, a trend line draws across their tops and KPI chips float in;
	// the numbers count up. Static markup only (labels go through textContent).
	const BAR_HEIGHTS = [52, 70, 64, 90, 104, 124, 146];
	const growth_svg = () => {
		const bars = BAR_HEIGHTS.map((h, i) => {
			const x = 14 + i * 56;
			const fill = i === BAR_HEIGHTS.length - 1 ? "url(#ts-growth-bar-hi)" : "url(#ts-growth-bar)";
			return `<rect class="ts-growth-bar" style="--i:${i}" x="${x}" y="${160 - h}" width="30" height="${h}" rx="7" fill="${fill}"></rect>`;
		}).join("");
		const pts = BAR_HEIGHTS.map((h, i) => `${29 + i * 56},${160 - h - 10}`);
		const last = pts[pts.length - 1].split(",");
		return `<svg class="ts-growth-chart" viewBox="0 -18 400 192" preserveAspectRatio="xMidYMid meet" aria-hidden="true" focusable="false">
			<defs>
				<linearGradient id="ts-growth-bar" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff" stop-opacity="0.9"></stop><stop offset="1" stop-color="#ffffff" stop-opacity="0.22"></stop></linearGradient>
				<linearGradient id="ts-growth-bar-hi" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#86efac"></stop><stop offset="1" stop-color="#22c55e" stop-opacity="0.55"></stop></linearGradient>
				<linearGradient id="ts-growth-area" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#4ade80" stop-opacity="0.32"></stop><stop offset="1" stop-color="#4ade80" stop-opacity="0"></stop></linearGradient>
			</defs>
			<g class="ts-growth-grid"><line x1="0" x2="400" y1="40" y2="40"></line><line x1="0" x2="400" y1="80" y2="80"></line><line x1="0" x2="400" y1="120" y2="120"></line><line class="is-base" x1="0" x2="400" y1="160" y2="160"></line></g>
			<polygon class="ts-growth-area" points="${pts.join(" ")} ${last[0]},160 29,160" fill="url(#ts-growth-area)"></polygon>
			${bars}
			<polyline class="ts-growth-line" points="${pts.join(" ")}" pathLength="1"></polyline>
			<circle class="ts-growth-ring" cx="${last[0]}" cy="${last[1]}" r="6"></circle>
			<circle class="ts-growth-dot" cx="${last[0]}" cy="${last[1]}" r="5"></circle>
		</svg>`;
	};

	const ICONS = {
		up: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8"></path></svg>',
		orders: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h2l2.2 10.2a1 1 0 0 0 1 .8h7.6a1 1 0 0 0 1-.8L19 8H7"></path><circle cx="10" cy="19.5" r="1.2"></circle><circle cx="17" cy="19.5" r="1.2"></circle></svg>',
		users: '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="9" cy="8" r="3.2"></circle><path d="M3.5 19a5.5 5.5 0 0 1 11 0M16 5.2a3.2 3.2 0 0 1 0 6.1M17.5 13.6A5.5 5.5 0 0 1 20.5 19"></path></svg>',
	};

	// <span data-ts-count data-to data-decimals data-prefix data-suffix data-group>final</span>
	const counter = (to, { decimals = 0, prefix = "", suffix = "", group = false } = {}) => {
		const span = el("span", "ts-growth-num");
		span.setAttribute("data-ts-count", "");
		span.dataset.to = String(to);
		span.dataset.decimals = String(decimals);
		span.dataset.prefix = prefix;
		span.dataset.suffix = suffix;
		span.dataset.group = group ? "1" : "";
		span.textContent = format_count(to, span.dataset);
		return span;
	};

	function format_count(value, d) {
		const decimals = parseInt(d.decimals || "0", 10);
		const fixed = Number(value).toFixed(decimals);
		const shown = d.group ? Number(fixed).toLocaleString("en-IN", { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : fixed;
		return `${d.prefix || ""}${shown}${d.suffix || ""}`;
	}

	const build_growth = () => {
		const wrap = el("div", "ts-login-growth");
		wrap.setAttribute("aria-hidden", "true");

		const card = el("div", "ts-growth-card");
		const head = el("div", "ts-growth-head");
		const head_text = el("div", "ts-growth-head-text");
		head_text.appendChild(el("span", "ts-growth-label", t("Business growth")));
		const value_row = el("div", "ts-growth-value-row");
		const value = el("strong", "ts-growth-value");
		value.appendChild(counter(12.4, { decimals: 1, prefix: "₹", suffix: "L" }));
		value_row.appendChild(value);
		const pill = el("span", "ts-growth-pill");
		pill.innerHTML = ICONS.up; // static markup
		pill.appendChild(counter(38, { prefix: "+", suffix: "%" }));
		value_row.appendChild(pill);
		head_text.appendChild(value_row);
		head.appendChild(head_text);
		card.appendChild(head);

		const chart = el("div", "ts-growth-chart-wrap");
		chart.innerHTML = growth_svg(); // static markup (numbers computed above, no user data)
		card.appendChild(chart);

		const months = el("div", "ts-growth-months");
		["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct"].forEach((m) => months.appendChild(el("span", "", t(m))));
		card.appendChild(months);
		wrap.appendChild(card);

		const chip = (cls, icon, label, number, delta) => {
			const c = el("div", `ts-growth-chip ${cls}`);
			const i = el("span", "ts-growth-chip-icon");
			i.innerHTML = icon; // static markup
			c.appendChild(i);
			const body_ = el("span", "ts-growth-chip-body");
			body_.appendChild(el("span", "ts-growth-chip-label", label));
			const n = el("strong", "ts-growth-chip-value");
			n.appendChild(number);
			if (delta) n.appendChild(el("em", "ts-growth-chip-delta", delta));
			body_.appendChild(n);
			c.appendChild(body_);
			return c;
		};
		card.appendChild(chip("is-orders", ICONS.orders, t("Orders"), counter(1284, { group: true }), "▲ 18%"));
		card.appendChild(chip("is-customers", ICONS.users, t("New customers"), counter(96, { prefix: "+" }), ""));
		return wrap;
	};

	// Count the numbers up once the card has risen in; instant when motion is off.
	const run_counters = (root) => {
		const speed = parseFloat(getComputedStyle(body).getPropertyValue("--tl-speed")) || 0;
		const nodes = root.querySelectorAll("[data-ts-count]");
		if (!speed || !("requestAnimationFrame" in window)) return; // final values already shown
		const duration = 1600 * speed;
		const delay = 650 * speed;
		nodes.forEach((node) => {
			const to = parseFloat(node.dataset.to);
			node.textContent = format_count(0, node.dataset);
			const start = performance.now() + delay;
			const tick = (now) => {
				const p = Math.min(1, Math.max(0, (now - start) / duration));
				const eased = 1 - Math.pow(1 - p, 3);
				node.textContent = format_count(to * eased, node.dataset);
				if (p < 1) requestAnimationFrame(tick);
			};
			requestAnimationFrame(tick);
		});
	};

	// ---- brand panel -----------------------------------------------------------------------
	if (!page.querySelector(":scope > .ts-login-brand")) {
		const brand = el("aside", "ts-login-brand");
		brand.setAttribute("aria-label", t("Welcome"));

		["ts-login-layer ts-login-halo", "ts-login-layer ts-login-grid", "ts-login-layer ts-login-blob is-a", "ts-login-layer ts-login-blob is-b", "ts-login-layer ts-login-blob is-c"].forEach((cls) => {
			const layer = el("span", cls);
			layer.setAttribute("aria-hidden", "true");
			brand.appendChild(layer);
		});

		const top = el("div", "ts-login-brand-top");
		const logo_src = (page.querySelector(".page-card-head .app-logo") || {}).src;
		{
			// Full Thirvusoft wordmark on a white plate; falls back to the card logo
			// (a small square tile) if the wordmark cannot be loaded.
			const tile = el("span", "ts-login-brand-logo is-wordmark");
			const img = el("img");
			img.setAttribute("src", WORDMARK_URL);
			img.setAttribute("alt", "Thirvusoft Private Limited");
			img.addEventListener(
				"error",
				() => {
					if (!logo_src) return tile.remove();
					tile.classList.remove("is-wordmark");
					img.setAttribute("alt", "");
					img.setAttribute("src", logo_src);
				},
				{ once: true }
			);
			tile.appendChild(img);
			top.appendChild(tile);
		}
		top.appendChild(el("span", "ts-login-brand-tag", t("Secure sign-in")));
		brand.appendChild(top);

		const growth = build_growth();
		brand.appendChild(growth);

		const copy = el("div", "ts-login-brand-copy");
		copy.appendChild(el("h2", "", t("Welcome back")));
		copy.appendChild(
			el("p", "", t("Sign in to pick up where you left off. Sales, stock, accounts and your team, all in one place."))
		);
		const points = el("ul", "ts-login-points");
		[
			t("Everything your business runs on, in one workspace"),
			t("Real-time updates across every department"),
			t("Works beautifully on desktop and mobile"),
		].forEach((text) => {
			const li = el("li");
			const icon = el("span", "ts-login-point-icon");
			icon.appendChild(check_icon());
			li.appendChild(icon);
			li.appendChild(el("span", "", text));
			points.appendChild(li);
		});
		copy.appendChild(points);
		brand.appendChild(copy);

		page.insertBefore(brand, page.firstChild);
		if (getComputedStyle(growth).display !== "none") run_counters(growth);
	}

	// ---- Powered by Thirvusoft ---------------------------------------------------------------
	const wrapper = page.querySelector(".page-content-wrapper");
	if (wrapper && !wrapper.querySelector(".ts-login-powered")) {
		const powered = el("p", "ts-login-powered");
		const mark = el("span", "ts-login-powered-mark");
		mark.setAttribute("aria-hidden", "true");
		powered.appendChild(mark);
		powered.appendChild(document.createTextNode(t("Powered by") + " "));
		const link = el("a", "", "Thirvusoft Private Limited");
		link.setAttribute("href", POWERED_URL);
		link.setAttribute("target", "_blank");
		link.setAttribute("rel", "noopener noreferrer");
		powered.appendChild(link);
		wrapper.appendChild(powered);
	}

	// ---- busy spinner while login.js talks to the server -------------------------------------
	const BUSY = "ts-login-busy";
	const PRIMARY = [
		".page-card-actions .btn-login:not(.btn-login-option)",
		".page-card-actions .btn-signup",
		".page-card-actions .btn-primary",
	].join(",");
	let busy_btn = null;
	let busy_timer = null;

	const visible_primary = () => {
		const sections = page.querySelectorAll("section");
		for (const section of sections) {
			if (section.offsetParent === null) continue; // hidden section
			const btn = section.querySelector(PRIMARY);
			if (btn) return btn;
		}
		return null;
	};

	const clear_busy = () => {
		if (busy_btn) {
			busy_btn.classList.remove(BUSY);
			busy_btn.removeAttribute("aria-busy");
		}
		busy_btn = null;
		clearTimeout(busy_timer);
	};

	const set_busy = () => {
		const btn = visible_primary();
		if (!btn || btn === busy_btn) return;
		clear_busy();
		busy_btn = btn;
		btn.classList.add(BUSY);
		btn.setAttribute("aria-busy", "true");
		busy_timer = setTimeout(clear_busy, 20000); // never leave a spinner behind
	};

	// login.js sends every action (sign in, forgot, email link, signup) through frappe.call.
	// On website pages frappe-web does not set body[data-ajax-state], so wrap frappe.call:
	// busy from the request start until it settles (success redirects away anyway).
	const frappe_ns = window.frappe;
	if (frappe_ns && typeof frappe_ns.call === "function" && !frappe_ns.call.__ts_login_wrapped) {
		const original_call = frappe_ns.call;
		const wrapped = function () {
			const result = original_call.apply(this, arguments);
			try {
				set_busy();
				const settle = () => clear_busy();
				if (result && typeof result.always === "function") result.always(settle);
				else if (result && typeof result.finally === "function") result.finally(settle);
				else if (result && typeof result.then === "function") result.then(settle, settle);
				else settle();
			} catch (e) {
				clear_busy();
			}
			return result;
		};
		wrapped.__ts_login_wrapped = true;
		frappe_ns.call = wrapped;
	}

	// Fallback: desk-style request.js builds set body[data-ajax-state] on each request.
	if (!window.__ts_login_busy_observer && "MutationObserver" in window) {
		window.__ts_login_busy_observer = new MutationObserver(() => {
			if (body.getAttribute("data-ajax-state") === "triggered") set_busy();
			else if (busy_btn) clear_busy();
		});
		window.__ts_login_busy_observer.observe(body, { attributes: true, attributeFilter: ["data-ajax-state"] });
	}
})();
